/** Texts of manifest, permissions, audit and workflow. Pages have their own files next to this one. */
export const base = {
  name: 'Projects',
  description:
    'Projects with client, object, team, parties, services and phases; objects as buildings with their history.',
  nav: { projects: 'Projects', objects: 'Objects', types: 'Project types and phases' },
  entities: { project: 'Project', object: 'Object' },
  permissions: {
    project: {
      read: {
        label: 'View projects',
        description: 'Open projects; with scope "team" only projects of the own team.',
      },
      write: {
        label: 'Edit projects',
        description:
          'Create and change projects, maintain team, parties, services and phases. With scope "team" only projects of the own team.',
      },
      comment: { label: 'Comment on projects', description: 'Write comments on projects.' },
      remind: { label: 'Reminders on projects', description: 'Set own reminders on projects.' },
      export: { label: 'Export projects', description: 'Download the list of projects as CSV.' },
      archive: {
        label: 'Archive projects',
        description: 'Archive and restore projects, delete projects.',
      },
      share: {
        label: 'Share projects',
        description: 'Share projects with other companies of the group for reading.',
      },
    },
    object: {
      read: { label: 'View objects', description: 'Open buildings and properties.' },
      write: { label: 'Edit objects', description: 'Create and change objects.' },
      comment: { label: 'Comment on objects', description: 'Write comments on objects.' },
      export: {
        label: 'Export objects',
        description: 'Download the list of objects as a CSV file.',
      },
      archive: {
        label: 'Archive objects',
        description: 'Archive and restore objects, delete objects.',
      },
      plan: {
        label: 'Maintain plans',
        description:
          'Add, change and delete floor and site plans of objects, of projects with write access to the project.',
      },
    },
    settings: {
      manage: {
        label: 'Set up projects',
        description: 'Maintain project types, phase templates and the number sequences.',
      },
    },
  },
  roles: {
    reader: 'Projects: Read',
    editor: 'Projects: Edit',
    manager: 'Projects: Manage',
  },
  states: {
    acquisition: 'Acquisition',
    ordered: 'Ordered',
    active: 'In progress',
    paused: 'On hold',
    closed: 'Closed',
    cancelled: 'Cancelled',
  },
  transitions: {
    order: 'Ordered',
    start: 'Start work',
    pause: 'Put on hold',
    close: 'Close',
    cancel: 'Cancel',
  },
  sequences: { project: 'Project number', object: 'Object number' },
  sections: {
    customerProjects: 'Projects',
    customerObjects: 'Objects',
  },
  audit: {
    project: {
      create: 'Project created',
      update: 'Project changed',
      archive: 'Project archived',
      restore: 'Project restored',
      delete: 'Project deleted',
      anonymize: 'Project anonymised',
    },
    member: {
      add: 'Person added to the project team',
      update: 'Role in the project team changed',
      remove: 'Person removed from the project team',
    },
    distribution: {
      create: 'Distribution list created',
      update: 'Distribution list renamed',
      delete: 'Distribution list deleted',
      add: 'Recipients added to distribution list',
      remove: 'Recipient removed from distribution list',
    },
    participant: {
      add: 'Party added',
      update: 'Party changed',
      remove: 'Party removed',
    },
    service: {
      create: 'Service created',
      update: 'Service changed',
      delete: 'Service deleted',
    },
    phase: {
      create: 'Phase created',
      update: 'Phase changed',
      delete: 'Phase deleted',
    },
    object: {
      create: 'Object created',
      update: 'Object changed',
      archive: 'Object archived',
      restore: 'Object restored',
      delete: 'Object deleted',
      anonymize: 'Object anonymised',
      link: 'Object added to project',
      unlink: 'Object removed from project',
    },
    type: {
      create: 'Project type created',
      update: 'Project type changed',
      archive: 'Project type archived',
      restore: 'Project type restored',
    },
    template: {
      create: 'Phase template created',
      update: 'Phase template changed',
      archive: 'Phase template archived',
      restore: 'Phase template restored',
    },
  },
  guides: {
    projects: {
      title: 'Projects',
      summary: 'Create projects, maintain team, parties, services and phases.',
    },
    objects: {
      title: 'Objects',
      summary: 'Create buildings and see their projects over the years.',
    },
    types: {
      title: 'Project types and phases',
      summary: 'Set up the project types and phase templates of the company.',
    },
  },
  errors: {
    address: 'The address details are too long.',
    usage: 'The usage may have 100 characters at most.',
    notes: 'The notes may have 4000 characters at most.',
    specialBuilding: 'Please state whether it is a special building.',
    georeferenced: 'Please state whether findings are placed by GPS.',
    title: 'Please enter a title.',
    description: 'The description may have 4000 characters at most.',
    type: 'Please choose an active project type.',
    object: 'The object does not exist or is archived.',
    objectNotUsed: 'Projects of this type have no object.',
    objectLinked: 'The object already belongs to the project.',
    objectsLinked:
      'The project has objects. Remove them before choosing a project type without objects.',
    tooManyObjects: 'A project has at most 500 objects.',
    project: 'The project does not exist, or you may not change it.',
    client: 'Please choose a client that is available in Customers.',
    clientContact: 'The contact must belong to the client.',
    date: 'Please enter a valid date.',
    endsOn: 'The end must not be before the start.',
    anonymized: 'The project is anonymised and can no longer be changed.',
    account: 'Please choose an active account of the company.',
    memberRole: 'Please choose a role.',
    memberExists: 'This person is already in the team.',
    teamFull: 'A team has 100 people at most.',
    leadTaken: 'The project already has a project lead.',
    organization: 'Please choose an organisation that is available in Customers.',
    contact: 'The contact must belong to the organisation.',
    participantRole: 'Please choose a role.',
    distributionName: 'Please name the distribution list (at most 100 characters).',
    distributionExists: 'The project already has a distribution list with this name.',
    distributionsFull: 'A project has at most 50 distribution lists.',
    distributionFull: 'A distribution list holds at most 500 recipients.',
    distributionContact: 'A chosen contact is no longer available.',
    email: 'An email address is invalid.',
    recipients: 'Please give at least one recipient.',
    note: 'The note may have 500 characters at most.',
    nameTaken: 'This name already exists.',
    defaultsExist: 'The company already has project types.',
    templateMissing: 'The phase template does not exist or is archived.',
    noServices: 'Projects of this type have no services.',
    name: 'Please enter a name (200 characters at most).',
    phaseCode: 'The code may have 20 characters at most.',
    phases: 'A template needs 1 to 30 phases.',
    projectArchived: 'The project is archived. Restore it to change it.',
    objectName: 'Please enter a name for the object (at most 200 characters).',
    customerNumber: "The customer's object number may be at most 50 characters long.",
    countryCode: 'Please enter a two-letter country code, e.g. DE.',
    coordinates: 'Latitude and longitude go together.',
    buildingClass: 'The building class is between 1 and 5.',
    objectOrganization: 'Owner or operator is not available or has been merged.',
    objectInUse:
      'Projects still refer to this object, so it cannot be deleted. You can archive it instead.',
    objectAnonymized: 'The object is anonymised and can no longer be changed.',
    state: 'Choose a federal state from the list.',
  },
  offline: { projects: 'Projects', objects: 'Objects' },
};
